import { useCallback, useState } from 'react';
import {
    AiSuggestionItem,
    applyAiPrefillForPublicShare,
    runAiPrefillForPublicShare,
} from '../../../api/publicSessionApi';
import logger from '../../../utils/logger';

export type PrefillStatus = 'idle' | 'parsing' | 'review' | 'applying' | 'done' | 'error';

export interface PrefillState {
    status: PrefillStatus;
    suggestions: AiSuggestionItem[];
    errors: string[];
    processedBy: string | null;
    /** sessionQuestion-field-key -> accepted boolean */
    accepted: Record<string, boolean>;
    runPrefill: () => Promise<void>;
    toggleAccepted: (field: string, accepted: boolean) => void;
    acceptAll: () => void;
    rejectAll: () => void;
    applyAccepted: () => Promise<{ applied: number; skipped: number } | null>;
    reset: () => void;
}

/**
 * Orchestrates the two-step AI prefill flow:
 *   1. `runPrefill` — POST /ai-prefill, transitions to `review` with the
 *      suggestion list. All suggestions start accepted by default; worker
 *      can toggle individual ones off.
 *   2. `applyAccepted` — POST /ai-prefill/apply with the accepted subset.
 *      Returns counts. Transitions to `done` on success.
 */
export const usePublicSharePrefill = (
    publicShareId: string | undefined,
): PrefillState => {
    const [status, setStatus] = useState<PrefillStatus>('idle');
    const [suggestions, setSuggestions] = useState<AiSuggestionItem[]>([]);
    const [errors, setErrors] = useState<string[]>([]);
    const [processedBy, setProcessedBy] = useState<string | null>(null);
    const [accepted, setAccepted] = useState<Record<string, boolean>>({});

    const runPrefill = useCallback(async () => {
        if (!publicShareId) return;
        setStatus('parsing');
        setErrors([]);
        try {
            const response = await runAiPrefillForPublicShare(publicShareId);
            setSuggestions(response.suggestions);
            setErrors(response.errors);
            setProcessedBy(response.processedBy);
            const initialAccepted: Record<string, boolean> = {};
            for (const s of response.suggestions) {
                initialAccepted[s.field] = true;
            }
            setAccepted(initialAccepted);
            setStatus(response.suggestions.length > 0 ? 'review' : 'done');
        } catch (err: any) {
            logger.error('Run prefill failed', err);
            const message =
                err?.response?.data?.message ||
                err?.message ||
                'AI prefill failed. Please continue manually.';
            setErrors([message]);
            setStatus('error');
        }
    }, [publicShareId]);

    const toggleAccepted = useCallback((field: string, value: boolean) => {
        setAccepted((prev) => ({ ...prev, [field]: value }));
    }, []);

    const acceptAll = useCallback(() => {
        setAccepted((prev) => {
            const next = { ...prev };
            for (const s of suggestions) next[s.field] = true;
            return next;
        });
    }, [suggestions]);

    const rejectAll = useCallback(() => {
        setAccepted((prev) => {
            const next = { ...prev };
            for (const s of suggestions) next[s.field] = false;
            return next;
        });
    }, [suggestions]);

    const applyAccepted = useCallback(async () => {
        if (!publicShareId) return null;
        const acceptedItems = suggestions
            .filter((s) => accepted[s.field])
            .map((s) => ({ field: s.field, suggestedValue: s.suggestedValue }));
        if (acceptedItems.length === 0) {
            setStatus('done');
            return { applied: 0, skipped: 0 };
        }
        setStatus('applying');
        try {
            const result = await applyAiPrefillForPublicShare(publicShareId, acceptedItems);
            setStatus('done');
            return result;
        } catch (err: any) {
            logger.error('Apply prefill failed', err);
            const message =
                err?.response?.data?.message ||
                err?.message ||
                'Failed to save AI suggestions. You can still type the answers manually.';
            setErrors([message]);
            setStatus('error');
            return null;
        }
    }, [publicShareId, suggestions, accepted]);

    const reset = useCallback(() => {
        setStatus('idle');
        setSuggestions([]);
        setErrors([]);
        setProcessedBy(null);
        setAccepted({});
    }, []);

    return {
        status,
        suggestions,
        errors,
        processedBy,
        accepted,
        runPrefill,
        toggleAccepted,
        acceptAll,
        rejectAll,
        applyAccepted,
        reset,
    };
};
